<template>
  <v-layout column id="review-and-deliver" class="full-height-container">
    <IdentityVerificationSettingsDialog
      :title="identityVerificationDialogTitle"
      :opened.sync="identityVerificationSettingsDialogOpened"
      :taxReturnId="taxReturn.id"
      :companyId="taxReturn.company_id"
    />

    <v-layout column class="text-xs-left content">
      <v-layout column>
        <v-alert icon="info" :value="true" color="info" outline class="mb-2">
          <div>
            <p>
              For this messaging, you can use customized words (variables).
              These variables will then be replaced with the actual values when
              the message is sent. For example, "{entity_name}" will be replaced
              with "ABC Corp" if the entity's name is ABC Corp.
            </p>

            <p class="mb-0">
              All of the available customized words (variables) can be found
              <a
                href="https://helpcenter.soraban.com/customized-words-variables-new"
                style="cursor: pointer"
                target="_blank"
              >
                here (opens in a new tab).
              </a>
            </p>
          </div>
        </v-alert>

        <h2>Subject</h2>
        <v-text-field
          class="subject-input mt-1"
          v-model="subject"
          solo
          hide-details
          :disabled="!editable"
        />
        <v-checkbox
          class="pt-0 mt-2"
          label="Save for future use"
          v-model="saveSubjectForFutureUse"
          hide-details
          :disabled="!editable"
        />
      </v-layout>

      <v-layout column class="mt-4">
        <h2>Message</h2>

        <quill-editor
          v-model="message"
          :options="editorOption"
          class="delivery-message-input mt-2"
          :disabled="!editable"
        />

        <v-checkbox
          class="pt-0 mt-2"
          label="Save for future use"
          v-model="saveMessageForFutureUse"
          hide-details
          :disabled="!editable"
        />
      </v-layout>

      <v-layout column class="mt-4">
        <v-layout align-center justify-space-between>
          <h2>
            Recipients
          </h2>

          <v-menu>
            <v-btn
              slot="activator"
              small
              color="primary"
              class="text-none"
              :loading="updatingRecipient"
              :disabled="!editable"
            >
              <v-icon left small>add</v-icon>
              <span v-if="allRecipientNames.length === 0">
                Select Recipients
              </span>
              <span v-else>
                Select Additional Recipients
              </span>
            </v-btn>

            <v-list two-line subheader>
              <v-list-tile
                v-for="owner in currentCompany.owners"
                :key="owner.id"
                avatar
                @click="addRemoveRecipient(owner)"
                :disabled="
                  recipientNamesFromESignForms.some(
                    name => name === owner.full_name,
                  )
                "
              >
                <v-list-tile-action>
                  <v-checkbox
                    :input-value="
                      allRecipientNames.some(name => name === owner.full_name)
                    "
                    :disabled="
                      recipientNamesFromESignForms.some(
                        name => name === owner.full_name,
                      )
                    "
                  />
                </v-list-tile-action>

                <v-list-tile-content>
                  <v-list-tile-title>
                    {{ owner.full_name }}
                    <span v-if="owner.id === currentCompany.user.id">
                      (Primary contact)
                    </span>
                  </v-list-tile-title>
                  <v-list-tile-sub-title>{{
                    owner.email
                  }}</v-list-tile-sub-title>
                </v-list-tile-content>

                <v-list-tile-action
                  v-if="
                    recipientNamesFromESignForms.some(
                      name => name === owner.full_name,
                    )
                  "
                >
                  <v-tooltip>
                    <v-icon slot="activator" color="grey lighten-1"
                      >info</v-icon
                    >

                    <span>Required for e-sign forms.</span>
                  </v-tooltip>
                </v-list-tile-action>
              </v-list-tile>
            </v-list>
          </v-menu>
        </v-layout>

        <div v-if="allRecipientNames.length === 0">
          <span class="error--text">
            * No recipients have been added to this delivery yet. Please add a
            recipient to deliver the tax return.
          </span>
        </div>

        <div
          v-else
          v-for="(recipientName, index) in allRecipientNames"
          class="recipient-container"
        >
          <h3 class="px-3 py-3">{{ recipientName }}</h3>

          <v-divider></v-divider>

          <v-layout row wrap>
            <v-flex xs4 class="pa-3">
              <div class="font-weight-bold">Email:</div>

              <div
                class="pt-1 email"
                v-for="email in getEmailByName(recipientName)"
              >
                <span>{{ email }}</span>
              </div>
            </v-flex>

            <v-flex
              xs4
              class="pa-3"
              v-if="getESignFormsByName(recipientName).length > 0"
            >
              <span class="font-weight-bold">Pages to sign:</span>
              <ul class="pt-1">
                <li v-for="form in getESignFormsByName(recipientName)">
                  <span
                    v-if="
                      form.form_id === 'other' &&
                        !isEmpty(form.custom_form_name)
                    "
                    >{{ form.custom_form_name }}</span
                  >
                  <span v-else-if="getFormDefinitionById(form.form_id)">
                    {{ getFormDefinitionById(form.form_id).name }} ({{
                      form.form_id
                    }})
                  </span>
                  <span v-else>Unknown</span>
                </li>
              </ul>
            </v-flex>
          </v-layout>
        </div>
      </v-layout>

      <v-layout column class="mt-4" v-if="taxReturn.k1_packages.length > 0">
        <v-layout align-center justify-space-between>
          <h2>
            K-1 Packages
          </h2>
        </v-layout>

        <v-data-table
          :headers="k1TableHeaders"
          :items="taxReturn.k1_packages"
          class="partners-table auto-shrink"
          :pagination.sync="k1TablePagination"
          hide-actions
        >
          <template v-slot:items="props">
            <td>
              {{ props.item.partner_name }}
            </td>

            <td class="text-xs-left">
              {{ props.item.partner_email }}
            </td>

            <td class="text-xs-left">
              {{ props.item.custom_package_name }}
            </td>
          </template>
        </v-data-table>
      </v-layout>

      <v-layout column class="mt-4" v-if="hasOptions">
        <h2>Options</h2>

        <div class="mb-1 mt-3">
          <v-checkbox
            v-if="taxReturnStripeIDVEnabled"
            label="Require identity verification via Soraban Identity Fraud Protection"
            v-model="taxReturn.stripe_idv_enabled"
            :disabled="!editable"
            hide-details
            class="mt-0 pt-0 align-center"
          >
          </v-checkbox>
        </div>

        <div class="mb-3">
          <v-checkbox
            v-if="
              taxReturnIdentityVerificationEnabled &&
                identityVerifications.length > 0 &&
                !taxReturnStripeIDVEnabled
            "
            label="Enable identity verification via SMS"
            v-model="taxReturn.identity_verification_enabled"
            :disabled="!editable"
            hide-details
            class="mt-0 pt-0 align-center"
          >
            <div slot="append">
              <v-btn
                class="text-none"
                :disabled="
                  !taxReturn.identity_verification_enabled || !editable
                "
                @click="openIdentityVerificationSettingsDialog"
              >
                <v-icon class="mr-2 grey--text">settings</v-icon>
                Configure Identity Verification
              </v-btn>
            </div>
          </v-checkbox>
        </div>

        <v-layout class="mb-3" align-center>
          <v-checkbox
            v-if="taxReturnPaymentEnabled && billingEnabled && stripeConnected"
            label="Enable Stripe payment"
            :input-value="Boolean(taxReturn.payment)"
            @change="togglePayment"
            :disabled="!editable"
            hide-details
            class="mt-0 pt-0 payment-checkbox"
          />

          <v-layout
            v-if="taxReturn.payment"
            align-center
            class="payment-amount-container"
          >
            <v-divider vertical class="mx-3 my-2" />
            <span class="mr-2">Enter payment amount:</span>

            <CurrencyInput
              :value="taxReturn.payment.amount / 100"
              @input="$e => parseInt((taxReturn.payment.amount = $e * 100))"
              :options="{ currency: 'USD' }"
              :disabled="!editable"
              class="default-input"
              :class="{
                error: !taxReturn.payment.amount,
              }"
            />
            <v-spacer />
          </v-layout>
        </v-layout>
      </v-layout>

      <v-layout v-if="taxReturn.status === 'sent'" class="mt-3">
        <v-alert
          color="success"
          icon="check_circle"
          :value="true"
          outline
          style="flex: auto"
        >
          <b>This tax return has been delivered.</b>
        </v-alert>
      </v-layout>

      <v-btn
        class="ma-0 mt-3 mb-3 deliver-btn text-none"
        large
        color="primary"
        :disabled="!isDeliverable"
        @click="deliver"
      >
        Deliver
        <v-icon class="ml-2 send-icon">send</v-icon>
      </v-btn>

      <v-spacer style="min-height: 50px" />
    </v-layout>
  </v-layout>
</template>

<script>
import uniq from 'lodash/uniq';
import API from '@/api';
import axios from 'axios';
import FirmPreferenceMixins from '@/mixins/FirmPreferenceMixins';
import { mapGetters } from 'vuex';
import IdentityVerificationSettingsDialog from '@/components/returns/IdentityVerificationSettingsDialog.vue';
import TaxReturnIdentityVerificationMixin from '@/mixins/TaxReturnIdentityVerificationMixin';
import CurrencyInput from '@/components/returns/CurrencyInput';
import isEmpty from 'lodash/isEmpty';
// import posthog from 'posthog-js';

export default {
  mixins: [FirmPreferenceMixins, TaxReturnIdentityVerificationMixin],

  components: {
    IdentityVerificationSettingsDialog,
    CurrencyInput,
  },

  props: {
    currentCompany: {
      type: Object,
      required: true,
    },

    taxReturn: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      formDefinitions: [],
      delivering: false,
      delivered: false,

      editorOption: {
        formats: ['bold', 'italic', 'underline', 'link', 'indent', 'list'],
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline'],
            [{ indent: '-1' }, { indent: '+1' }],
            ['link'],
            [{ list: 'ordered' }, { list: 'bullet' }],
          ],
        },
        placeholder: 'Message',
        bounds: '.delivery-message-input',
      },

      subject: '',
      message: '',
      saveSubjectForFutureUse: false,
      saveMessageForFutureUse: false,

      identityVerificationSettingsDialogOpened: false,
      identityVerificationDialogTitle: undefined,
      identityVerifications: [],

      updatingRecipient: false,
      additionalRecipients: [],

      stripeConnected: false,

      // k1 packages
      k1TableHeaders: [
        {
          text: 'Partner name',
          align: 'left',
          value: 'partner_name',
          sortable: false,
        },
        {
          text: 'Partner email',
          value: 'partner_email',
          align: 'left',
          sortable: false,
        },
        {
          text: 'K-1 package',
          value: 'custom_package_name',
          align: 'left',
          sortable: false,
        },
      ],

      k1TablePagination: {
        rowsPerPage: -1,
      },
    };
  },

  computed: {
    ...mapGetters({
      currentFirm: 'currentFirm',
      currentUser: 'user',
    }),

    recipientNamesFromESignForms() {
      return uniq(
        this.taxReturn.e_sign_forms.flatMap(form => {
          return form.signatures
            .filter(signature => signature.id !== this.currentUser.id)
            .map(signature => signature.name);
        }),
      );
    },

    allRecipientNames() {
      return uniq(
        this.recipientNamesFromESignForms
          .concat(
            this.additionalRecipients.map(
              recipient => recipient.user.full_name,
            ),
          )
          .filter(user => user.id !== this.currentUser.id),
      );
    },

    editable() {
      return this.taxReturn.status === 'action_required';
    },

    hasRecipients() {
      return this.allRecipientNames.length > 0;
    },

    hasOptions() {
      return (
        this.taxReturnIdentityVerificationEnabled ||
        this.taxReturnStripeIDVEnabled
      );
    },

    isPaymentValid() {
      return (
        !this.taxReturn.payment ||
        (this.taxReturn.payment && this.taxReturn.payment.amount)
      );
    },

    isDeliverable() {
      return (
        this.editable &&
        !this.delivering &&
        this.hasRecipients &&
        this.isPaymentValid
      );
    },
  },

  methods: {
    isEmpty,

    isRequiredToSignForms(recipientName) {
      return this.recipientNamesFromESignForms.some(
        name => name === recipientName,
      );
    },

    getEmailByName(name) {
      return uniq(
        this.taxReturn.e_sign_forms
          .flatMap(form => {
            return form.signatures
              .filter(
                signature =>
                  signature.name === name &&
                  signature.userId !== this.currentUser.id,
              )
              .map(signature => {
                const user = this.currentCompany.owners.find(
                  user => user.id === signature.userId,
                );

                return user.email;
              });
          })
          .concat(
            this.additionalRecipients
              .filter(recipient => recipient.user.full_name === name)
              .map(recipient => recipient.user.email),
          ),
      );
    },

    getESignFormsByName(name) {
      return this.taxReturn.e_sign_forms.filter(form => {
        return form.signatures.some(signature => signature.name === name);
      });
    },

    getFormDefinitionById(id) {
      return this.formDefinitions.find(form => form.id === id);
    },

    async getFormDefinitions() {
      const response = await API.signatureRequest.getFormDefinitions();

      this.formDefinitions = response.data.concat({
        id: 'other',
        name: 'Other',
        taxpayer_signature: null,
        spouse_signature: null,
      });
    },

    async openIdentityVerificationSettingsDialog($e, title) {
      this.identityVerificationSettingsDialogOpened = true;
      this.identityVerificationDialogTitle = !isEmpty(title)
        ? title
        : undefined;
    },

    async saveIdentityVerificationEnabled() {
      await axios.patch(`/tax_returns/${this.taxReturn.id}`, {
        company_id: this.taxReturn.company_id,
        identity_verification_enabled: this.taxReturn
          .identity_verification_enabled,
      });
    },
    async saveStripeIDVEnabled() {
      await axios.patch(`/tax_returns/${this.taxReturn.id}`, {
        company_id: this.taxReturn.company_id,
        stripe_idv_enabled: this.taxReturn.stripe_idv_enabled,
      });
    },

    async addRemoveRecipient(owner) {
      try {
        this.updatingRecipient = true;

        const existingRecipient = this.additionalRecipients.find(
          recipient => recipient.user.id === owner.id,
        );

        if (existingRecipient) {
          await API.taxReturnAdditionalRecipient.removeAdditionalRecipient({
            id: existingRecipient.id,
            taxReturnId: this.taxReturn.id,
            companyId: this.taxReturn.company_id,
          });
        } else {
          const response = await API.taxReturnAdditionalRecipient.addAdditionalRecipient(
            {
              taxReturnId: this.taxReturn.id,
              companyId: this.taxReturn.company_id,
              userId: owner.id,
            },
          );
        }
      } finally {
        this.updatingRecipient = false;

        await this.getAdditionalRecipients();
      }
    },

    async getAdditionalRecipients() {
      const response = await API.taxReturnAdditionalRecipient.listAdditionalRecipients(
        {
          taxReturnId: this.taxReturn.id,
          companyId: this.taxReturn.company_id,
        },
      );

      this.additionalRecipients = response.data;
    },

    async deliver() {
      try {
        this.delivering = true;

        await this.checkIdentityVerificationsForUpdate(
          this.taxReturn.id,
          this.taxReturn.company_id,
        );

        if (
          this.taxReturn.identity_verification_enabled &&
          this.hasUnverifiedIdentityVerifications
        ) {
          this.openIdentityVerificationSettingsDialog(
            null,
            'Please confirm the information below and try delivery again.',
          );
        } else {
          await axios.post(`/tax_returns/${this.taxReturn.id}/deliver`, {
            company_id: this.taxReturn.company_id,
            subject: this.subject,
            message: this.message,
            save_subject_for_future_use: this.saveSubjectForFutureUse,
            save_message_for_future_use: this.saveMessageForFutureUse,
            payment: this.taxReturn.payment,
          });

          // posthog.capture('delivered tax return', {
          //   tax_return_id: this.taxReturn.id,
          //   company_id: this.currentCompany.id,
          //   identity_verification_enabled: this.taxReturn
          //     .identity_verification_enabled,
          //   payment_enabled: this.taxReturn.payment ? true : false,
          // });

          // If save subject or message for future use is checked,
          // the firm preferences will need to be updated.
          if (this.saveSubjectForFutureUse || this.saveMessageForFutureUse) {
            this.$store.dispatch('setCurrentFirm');
          }

          this.delivered = true;
          this.$emit('return-delivered');
        }
      } finally {
        this.delivering = false;
      }
    },

    togglePayment(enabled) {
      if (enabled) {
        this.taxReturn.payment = {
          amount: 0,
          currency: 'usd',
        };
      } else {
        this.taxReturn.payment = null;
      }
    },

    reset() {
      this.saveSubjectForFutureUse = false;
      this.saveMessageForFutureUse = false;

      this.subject = this.taxReturn.subject
        ? this.taxReturn.subject
        : this.taxReturnDeliveryDefaultSubject
        ? this.taxReturnDeliveryDefaultSubject
        : 'Your {tax_year} tax return is ready for review';

      this.message = this.taxReturn.message
        ? this.taxReturn.message
        : this.taxReturnDeliveryDefaultMessage
        ? this.taxReturnDeliveryDefaultMessage
        : `<p>Hi {client_preferred_name},</p>
<p>We are pleased to inform you that your {tax_year} Tax Return is now available for your review. Kindly take a moment to review and sign the return by clicking on the link below. The link will also display any outstanding balance owed to the tax authority.</p>
            <p>Once we receive the e-file authorization form (and payment), we will proceed with e-filing the tax return.</p>
            <p>Thank you,</p>
            <p>${this.currentFirm.name}</p>`;

      // Workaround fix for SOR-10941
      // Force re-render this vue component
      const element = document.getElementById('review-and-deliver');
      element.style.display = 'none';

      requestAnimationFrame(() => {
        element.style.display = 'flex';
      });
    },

    async checkStripeIntegration() {
      try {
        const response = await API.integration.checkStripeIntegration();

        this.stripeConnected = response.data.connected;
      } catch (error) {
        console.error(error);
        Sentry.captureException(error);
      }
    },

    async save() {
      if (!this.editable || this.delivered) {
        return;
      }

      const response = await API.taxReturn.updateTaxReturn({
        id: this.taxReturn.id,
        companyId: this.taxReturn.company_id,
        subject: this.subject,
        message: this.message,
        save_subject_for_future_use: this.saveSubjectForFutureUse,
        save_message_for_future_use: this.saveMessageForFutureUse,
      });

      this.taxReturn.subject = response.data.subject;
      this.taxReturn.message = response.data.message;

      // If save subject or message for future use is checked,
      // the firm preferences will need to be updated.
      if (this.saveSubjectForFutureUse || this.saveMessageForFutureUse) {
        this.$store.dispatch('setCurrentFirm');
      }

      this.$emit('return-updated', response.data);
    },
  },

  watch: {
    'taxReturn.identity_verification_enabled'(value) {
      this.saveIdentityVerificationEnabled();

      if (value) {
        this.openIdentityVerificationSettingsDialog();
      }
    },
    'taxReturn.stripe_idv_enabled'(value) {
      this.saveStripeIDVEnabled();
    },
  },

  async beforeDestroy() {
    await this.save();
  },

  async created() {
    await this.getFormDefinitions();

    await this.getAdditionalRecipients();

    await this.checkIdentityVerificationsForUpdate(
      this.taxReturn.id,
      this.taxReturn.company_id,
    );

    await this.checkStripeIntegration();

    this.reset();
  },
};
</script>

<style lang="postcss" scoped>
.full-height-container {
  box-sizing: border-box;
  overflow: auto;
  min-height: calc(100vh - 60px);
  max-height: calc(100vh - 60px);
  background-color: var(--gray-100);
  border-top: 1px solid var(--gray-200);
}

.content {
  margin: 0 auto;
  max-width: 800px;
}

.subject-input {
  &.v-input {
    border: 1px solid #ccc;
  }

  & >>> .v-input__slot {
    box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.15) !important;
  }
}

.delivery-message-input {
  overflow: visible;

  & >>> .ql-container {
    height: max-content;
  }

  & >>> .ql-container .ql-editor {
    min-height: 200px;
    max-height: 200px;
    background-color: #fff;
  }

  & >>> .ql-container .ql-editor p,
  & >>> .ql-container .ql-editor ul {
    padding-bottom: 8px;
  }
}

.recipient-container {
  margin-top: 10px;
  border: 1px solid #bbb;
  border-radius: 4px;
  background-color: #fff;
  font-size: 16px;

  & >>> h3 {
    font-size: 20px;
  }
}

.email {
  word-break: break-all; /* Breaks long words or emails to the next line */
  white-space: normal; /* Ensure text wraps if needed */
}

.deliver-btn {
  font-weight: bold;
  font-size: 19px;
}

.send-icon {
  opacity: 0.6;
}

.payment-checkbox {
  flex-grow: 0;
}

.payment-amount-container {
  & >>> .v-input {
    border: 1px solid #ccc;
  }

  & >>> .v-input.error {
    border: 1px solid red;
  }
}

.partners-table {
  border: 1px solid #bbb;
  border-radius: 4px;
  margin: 8px 0px;

  & >>> .v-table tbody td,
  & >>> .v-table thead th {
    padding: 0px 10px;
  }
}
</style>
